import React, { useState } from "react";
import axios from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./propertymanagement.css";

const managementServices = [
  {
    icon: "far fa-comments",
    title: "Tenant Communication",
    description:
      "Responsive support for tenant questions, requests, and day-to-day property matters.",
  },
  {
    icon: "fas fa-coins",
    title: "Rent Follow-Up",
    description:
      "Organised rent collection, payment reminders, and clear updates for property owners.",
  },
  {
    icon: "far fa-file-alt",
    title: "Lease Coordination",
    description:
      "Practical support with lease documentation, renewals, and important tenancy dates.",
  },
  {
    icon: "fas fa-tools",
    title: "Maintenance Coordination",
    description:
      "Repair requests are assessed, assigned, and followed through with trusted specialists.",
  },
  {
    icon: "fas fa-search-location",
    title: "Property Inspections",
    description:
      "Regular condition monitoring helps identify concerns early and protect your property.",
  },
  {
    icon: "far fa-chart-bar",
    title: "Owner Updates",
    description:
      "Stay informed through clear communication and ongoing supervision of your investment.",
  },
];

const benefits = [
  "Faster communication with owners and tenants",
  "Consistent follow-up from a professional team",
  "Better coordination for maintenance and daily issues",
  "A simpler, more efficient management process",
  "Property care focused on long-term client satisfaction",
];

function PropertyManagement() {
  const API = process.env.REACT_APP_API_URL || "https://nvrealestate.ae";
  const [formData, setFormData] = useState({
    agent_name: "",
    agent_phone: "",
    agent_email: "",
    property_type: "",
    agent_address: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState({ text: "", type: "" });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const handlePhoneChange = (phone) => {
    setFormData((current) => ({ ...current, agent_phone: phone }));
    setErrors((current) => ({ ...current, agent_phone: "" }));
  };

  const validateForm = () => {
    const nextErrors = {};
    const phoneDigits = formData.agent_phone.replace(/\D/g, "");

    if (!formData.agent_name.trim()) nextErrors.agent_name = "Full name is required";
    if (!phoneDigits) nextErrors.agent_phone = "Phone number is required";
    else if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      nextErrors.agent_phone = "Enter a valid phone number";
    }
    if (!formData.agent_email.trim()) nextErrors.agent_email = "Email address is required";
    else if (!/\S+@\S+\.\S+/.test(formData.agent_email)) {
      nextErrors.agent_email = "Enter a valid email address";
    }
    if (!formData.property_type) nextErrors.property_type = "Select a property type";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setResponse({ text: "", type: "" });
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        proref: "Property Management: Request a Callback",
        current_url: window.location.href,
        agent_name: formData.agent_name,
        agent_lname: "-",
        agent_phone: formData.agent_phone,
        agent_email: formData.agent_email,
        agent_address: [
          `Property type: ${formData.property_type}`,
          formData.agent_address,
        ]
          .filter(Boolean)
          .join("\n"),
      };

      const result = await axios.post(
        `${API}/api/EnquiryHandlerStatic.ashx`,
        JSON.stringify(payload),
        { headers: { "Content-Type": "application/json" } }
      );

      setResponse({
        text:
          result.data?.message ||
          "Thank you. Our property management team will contact you shortly.",
        type: "success",
      });
      setFormData({
        agent_name: "",
        agent_phone: "",
        agent_email: "",
        property_type: "",
        agent_address: "",
      });
      setErrors({});
    } catch (error) {
      setResponse({
        text: "Something went wrong. Please try again or contact us on WhatsApp.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pm-page">
      <section
        className="pm-hero"
        style={{
          backgroundImage:
            'linear-gradient(90deg, rgb(59 71 85 / 96%) 0%, rgb(29 36 43 / 89%) 42%, rgba(11, 23, 37, 0.18) 78%), url("/images/inner-banner/Property-Management.jpg")',
        }}
      >
        <div className="pm-shell">
          <div className="pm-hero-content">
            <span className="pm-eyebrow">Property care without the daily pressure</span>
            <h1>Property Management in Abu Dhabi</h1>
            <p className="pm-hero-lead">
              Fast support, clear communication, and reliable management for your
              property.
            </p>
            <p className="pm-hero-copy">
              Manage your property with confidence through a responsive team that
              helps with tenant communication, rent follow-up, maintenance
              coordination, and day-to-day property matters.
            </p>
            <div className="pm-actions">
              <a
                className="pm-button pm-button-primary"
                href="https://wa.me/971501577822?text=Hello%2C%20I%20would%20like%20help%20managing%20my%20property."
                target="_blank"
                rel="noreferrer"
              >
                <i className="fab fa-whatsapp" /> Send a WhatsApp Message
              </a>
              <a className="pm-button pm-button-light" href="#property-management-form">
                Request Property Management
              </a>
              <a className="pm-button pm-button-light" href="tel:+97126446368">
                <i className="fas fa-phone-alt" /> Talk to Our Team
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="pm-intro pm-section">
        <div className="pm-shell pm-intro-grid">
          <div className="pm-intro-image">
            <img
              src="/assets/images/qwdeq.webp"
              alt="Property owner meeting with Nova Vision property management advisors"
            />
            <div className="pm-image-note">
              <strong>Local support</strong>
              <span>For owners in the UAE and overseas</span>
            </div>
          </div>
          <div className="pm-intro-content">
            <span className="pm-section-label">A simpler ownership experience</span>
            <h2>Your property should be rewarding, not stressful.</h2>
            <p>
              Our property management service in Abu Dhabi is designed to help
              landlords and investors save time, stay informed, and keep their
              properties running smoothly.
            </p>
            <p>
              From handling tenant requests to coordinating maintenance and
              following up on rent, we focus on quick communication and practical
              support. Our goal is to protect your property, reduce hassle, and
              improve the ownership experience.
            </p>
            <div className="pm-property-types">
              <span><i className="far fa-building" /> Apartments</span>
              <span><i className="fas fa-home" /> Villas</span>
              <span><i className="fas fa-chart-line" /> Investments</span>
            </div>
          </div>
        </div>
      </section>

      <section className="pm-services pm-section">
        <div className="pm-shell">
          <header className="pm-section-heading">
            <span className="pm-section-label">What we manage</span>
            <h2>Complete Property Management Services</h2>
            <p>
              Reliable support for apartments, villas, and investment properties
              across Abu Dhabi.
            </p>
          </header>
          <div className="pm-service-grid">
            {managementServices.map((service, index) => (
              <article className="pm-service-card" key={service.title}>
                <span className="pm-service-number">0{index + 1}</span>
                <span className="pm-service-icon"><i className={service.icon} /></span>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </article>
            ))}
          </div>
          <p className="pm-services-note">
            Whether you are a local owner or an overseas investor, our team helps
            you stay connected to your property without managing every detail
            yourself.
          </p>
        </div>
      </section>

      <section
        className="pm-why pm-section"
        style={{
          backgroundImage:
            'linear-gradient(90deg, rgb(59 71 85 / 96%) 0%, rgb(29 36 43 / 89%) 42%, rgba(11, 23, 37, 0.18) 78%), url(/images/inner-banner/Property-Management.jpg)',
        }}
      >
        <div className="pm-shell pm-why-grid">
          <div className="pm-why-copy">
            <span className="pm-section-label pm-section-label-light">Why choose us</span>
            <h2>Fast response matters.</h2>
            <p>
              Property management is not only about administration. It is about
              staying available, solving issues quickly, and making communication
              easier for both owners and tenants.
            </p>
            <a className="pm-button pm-button-primary" href="#property-management-form">
              Start Managing Your Property Better
            </a>
          </div>
          <div className="pm-benefit-list">
            {benefits.map((benefit) => (
              <div className="pm-benefit" key={benefit}>
                <span><i className="fas fa-check" /></span>
                <p>{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pm-contact pm-section" id="property-management-form">
        <div className="pm-shell pm-contact-card">
          <div className="pm-contact-copy">
            <span className="pm-section-label">Talk to a property manager</span>
            <h2>Need help managing your property in Abu Dhabi?</h2>
            <p>
              Send us your details and our team will contact you shortly with
              practical next steps for your property.
            </p>
            <div className="pm-contact-detail">
              <i className="fab fa-whatsapp" />
              <div>
                <span>Prefer a quick conversation?</span>
                <a
                  href="https://wa.me/971501577822"
                  target="_blank"
                  rel="noreferrer"
                >
                  WhatsApp +971 50 157 7822
                </a>
              </div>
            </div>
          </div>

          <form className="pm-form" onSubmit={handleSubmit} noValidate>
            <div className="pm-field">
              <label htmlFor="pm-name">Full Name *</label>
              <input
                id="pm-name"
                name="agent_name"
                value={formData.agent_name}
                onChange={handleChange}
                placeholder="Enter your full name"
                className={errors.agent_name ? "input-error" : ""}
              />
              {errors.agent_name && <small>{errors.agent_name}</small>}
            </div>

            <div className="pm-field">
              <label htmlFor="pm-phone">Phone Number *</label>
              <PhoneInput
                country="ae"
                value={formData.agent_phone}
                onChange={handlePhoneChange}
                inputClass={errors.agent_phone ? "input-error" : ""}
                containerClass="pm-phone-input"
                inputProps={{ id: "pm-phone" }}
                enableSearch
              />
              {errors.agent_phone && <small>{errors.agent_phone}</small>}
            </div>

            <div className="pm-field">
              <label htmlFor="pm-email">Email Address *</label>
              <input
                id="pm-email"
                type="email"
                name="agent_email"
                value={formData.agent_email}
                onChange={handleChange}
                placeholder="Enter your email address"
                className={errors.agent_email ? "input-error" : ""}
              />
              {errors.agent_email && <small>{errors.agent_email}</small>}
            </div>

            <div className="pm-field">
              <label htmlFor="pm-property-type">Property Type *</label>
              <select
                id="pm-property-type"
                name="property_type"
                value={formData.property_type}
                onChange={handleChange}
                className={errors.property_type ? "input-error" : ""}
              >
                <option value="">Select property type</option>
                <option value="Apartment">Apartment</option>
                <option value="Villa">Villa</option>
                <option value="Townhouse">Townhouse</option>
                <option value="Commercial property">Commercial property</option>
                <option value="Multiple properties">Multiple properties</option>
              </select>
              {errors.property_type && <small>{errors.property_type}</small>}
            </div>

            <div className="pm-field pm-field-full">
              <label htmlFor="pm-message">Message</label>
              <textarea
                id="pm-message"
                name="agent_address"
                rows="4"
                value={formData.agent_address}
                onChange={handleChange}
                placeholder="Tell us a little about your property"
              />
            </div>

            {response.text && (
              <p className={`pm-response ${response.type}`} role="status">
                {response.text}
              </p>
            )}

            <button className="pm-submit" type="submit" disabled={loading}>
              {loading ? "Sending..." : "Request a Callback"}
              {!loading && <i className="fas fa-arrow-right" />}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

export default PropertyManagement;
